import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Edit, Trash2, MoveUp, MoveDown, Loader2, ImageIcon, Globe, Film } from 'lucide-react'
import { useState, useEffect } from 'react'
import { heroSliderService, type HeroSlide, type LocalizedString } from '@/services/hero-slider'
import { MediaPicker } from '@/components/media/media-picker'
import { toast } from 'sonner'
import { getMediaUrl } from '@/utils/url-utils'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Hero Slider', href: '/hero-slider', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

const emptyLocalizedString: LocalizedString = {
  tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: ''
}

function HeroSliderPage() {
  const [slides, setSlides] = useState<HeroSlide[]>([])
  const [loading, setLoading] = useState(true)
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Form State
  const [title, setTitle] = useState<LocalizedString>({ ...emptyLocalizedString })
  const [description, setDescription] = useState<LocalizedString>({ ...emptyLocalizedString })
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null)
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false)
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    loadSlides()
  }, [])

  const loadSlides = async () => {
    try {
      setLoading(true)
      const data = await heroSliderService.getSlides({ active: false })
      setSlides(data.sort((a, b) => a.order - b.order))
    } catch (error) {
      console.error('Slider verileri yüklenemedi:', error)
      toast.error('Slider verileri yüklenemedi')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent, translate = false) => {
    e.preventDefault()

    // Validate
    if (!editingSlide && !mediaFile && !selectedMediaUrl) {
      toast.error('Lütfen bir görsel veya video seçin')
      return
    }
    if (!title.tr) {
      toast.error('Lütfen en azından Türkçe başlık girin')
      return
    }

    try {
      setSubmitting(true)
      const formData = new FormData()

      const submitTitle = Object.fromEntries(Object.entries(title).filter(([_, v]) => v.trim() !== ''))
      const submitDescription = Object.fromEntries(Object.entries(description).filter(([_, v]) => v.trim() !== ''))

      formData.append('title', JSON.stringify(submitTitle))
      formData.append('description', JSON.stringify(submitDescription))
      formData.append('isActive', String(isActive))

      if (mediaFile) {
        formData.append('media', mediaFile)
      } else if (selectedMediaUrl) {
        formData.append('mediaUrl', selectedMediaUrl)
      }

      if (editingSlide) {
        await heroSliderService.updateSlide(editingSlide._id, formData, translate)
        toast.success(translate ? 'Slide AI ile çevrildi ve güncellendi' : 'Slide başarıyla güncellendi')
      } else {
        await heroSliderService.createSlide(formData, translate)
        toast.success(translate ? 'Yeni slide AI ile çevrildi ve oluşturuldu' : 'Yeni slide oluşturuldu')
      }

      await loadSlides()
      setIsDialogOpen(false)
      resetForm()
    } catch (error) {
      console.error('İşlem başarısız:', error)
      toast.error('İşlem başarısız oldu')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Bu slide silinecek. Emin misiniz?')) {
      try {
        await heroSliderService.deleteSlide(id)
        toast.success('Slide silindi')
        await loadSlides()
      } catch (error) {
        console.error('Silme işlemi başarısız:', error)
        toast.error('Silme işlemi başarısız oldu')
      }
    }
  }

  const toggleStatus = async (slide: HeroSlide) => {
    // Optimistic Update
    const newIsActive = !slide.isActive
    const previousSlides = [...slides]
    setSlides(slides.map(s => s._id === slide._id ? { ...s, isActive: newIsActive } : s))

    try {
      toast.success(`Slide ${newIsActive ? 'aktif' : 'pasif'} ediliyor...`)
      const formData = new FormData()
      formData.append('isActive', String(newIsActive))
      await heroSliderService.updateSlide(slide._id, formData)

      // Still refresh to make sure we are in sync in bg, but user doesn't feel wait
      heroSliderService.getSlides({ active: false }).then(data => {
        setSlides(data.sort((a, b) => a.order - b.order))
      }).catch(console.error)
    } catch (error) {
      // Revert if API fails
      setSlides(previousSlides)
      console.error('Durum değiştirilemedi:', error)
      toast.error('Durum değiştirilemedi')
    }
  }

  const moveSlide = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = slides.findIndex(s => s._id === id)
    if (currentIndex === -1) return

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= slides.length) return

    // Optimistic update
    const previousSlides = [...slides]
    const reorderedSlides = [...slides]
    const [movedSlide] = reorderedSlides.splice(currentIndex, 1)
    reorderedSlides.splice(newIndex, 0, movedSlide)

    // update order numbers for UI immediately
    const updatedUI = reorderedSlides.map((slide, index) => ({
      ...slide,
      order: index
    }))
    setSlides(updatedUI)

    const slideOrders = updatedUI.map((slide) => ({
      id: slide._id,
      order: slide.order
    }))

    try {
      toast.success('Sıralama güncelleniyor...')
      await heroSliderService.updateSlideOrder(slideOrders)
    } catch (error) {
      // Revert if API fails
      setSlides(previousSlides)
      console.error('Sıralama güncellenemedi:', error)
      toast.error('Sıralama güncellenemedi')
    }
  }

  const resetForm = () => {
    setTitle({ ...emptyLocalizedString })
    setDescription({ ...emptyLocalizedString })
    setMediaFile(null)
    setSelectedMediaUrl(null)
    setIsActive(true)
    setEditingSlide(null)
  }

  const openEditDialog = async (slide: HeroSlide) => {
    try {
      const toastId = toast.loading('Slide detayları yükleniyor...')
      const fullSlide = await heroSliderService.getSlide(slide._id)
      toast.dismiss(toastId)

      setEditingSlide(fullSlide)
      // Handle both string and object responses safely
      const titleData = typeof fullSlide.title === 'string' ? { tr: fullSlide.title } : fullSlide.title
      const descData = typeof fullSlide.description === 'string' ? { tr: fullSlide.description } : fullSlide.description

      setTitle({ ...emptyLocalizedString, ...(titleData || {}) })
      setDescription({ ...emptyLocalizedString, ...(descData || {}) })
      setIsActive(fullSlide.isActive)
      setMediaFile(null)
      setSelectedMediaUrl(fullSlide.mediaUrl)
      setIsDialogOpen(true)
    } catch (error) {
      toast.error('Slide detayları alınamadı')
      console.error(error)
    }
  }

  if (loading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  const isVideo = (filename?: string | null) => {
    if (!filename) return false;
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(filename);
  };

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Hero Slider</h1>
            <p className="text-sm text-muted-foreground">Ana sayfa slider görsellerini yönetin</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            if (!open) resetForm()
            setIsDialogOpen(open)
          }}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="mr-2 h-4 w-4" />
                Yeni Slide
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSlide ? 'Slide Düzenle' : 'Yeni Slide Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">

                <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                  <Globe className="h-4 w-4 mt-0.5" />
                  <div>
                    <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                    Türkçe içerik (başlık ve açıklama) baz alınarak tüm diller otomatik olarak çevrilecektir. Maddelerin çokluğuna ve içeriğin uzunluğuna göre bu işlem biraz vakit alabilir, lütfen işlem tamamlanana kadar bekleyiniz.
                  </div>
                </div>

                <Tabs defaultValue="tr" className="w-full">
                  <TabsList className="w-full flex flex-wrap justify-start h-auto">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="flex-1 min-w-[80px]">
                        {lang.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor={`title-${lang.code}`}>Başlık ({lang.code.toUpperCase()})</Label>
                        <Input
                          id={`title-${lang.code}`}
                          value={title[lang.code as keyof LocalizedString] || ''}
                          onChange={(e) => setTitle({ ...title, [lang.code]: e.target.value })}
                          required={lang.code === 'tr'}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`desc-${lang.code}`}>Açıklama ({lang.code.toUpperCase()})</Label>
                        <Input
                          id={`desc-${lang.code}`}
                          value={description[lang.code as keyof LocalizedString] || ''}
                          onChange={(e) => setDescription({ ...description, [lang.code]: e.target.value })}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>

                <div className="space-y-2">
                  <Label htmlFor="media">Görsel / Video</Label>
                  <div className="flex gap-2">
                    <Input
                      id="media"
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => {
                        setMediaFile(e.target.files?.[0] || null)
                        setSelectedMediaUrl(null)
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsMediaPickerOpen(true)}
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Kütüphaneden Seç
                    </Button>
                  </div>
                  {(selectedMediaUrl || (editingSlide && !mediaFile)) && (
                    <div className="mt-2 p-2 border rounded-md bg-muted/50 flex items-center justify-between">
                      <div className="flex items-center gap-2 truncate">
                        {selectedMediaUrl ? (
                          <>
                            {isVideo(selectedMediaUrl) ? (
                              <div className="h-10 w-10 bg-black/10 rounded flex items-center justify-center">
                                <Film className="h-5 w-5 text-blue-500" />
                              </div>
                            ) : (
                              <img src={getMediaUrl(selectedMediaUrl)} alt="Selected" className="h-10 w-10 object-cover rounded" />
                            )}
                            <span className="text-xs truncate">{selectedMediaUrl.split('/').pop()}</span>
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            {isVideo(editingSlide?.mediaUrl) ? (
                              <div className="h-10 w-10 bg-black/10 rounded flex items-center justify-center">
                                <Film className="h-5 w-5 text-blue-500" />
                              </div>
                            ) : (
                              <img src={getMediaUrl(editingSlide?.mediaUrl || '')} alt="Current" className="h-10 w-10 object-cover rounded" />
                            )}
                            <span className="text-xs">Mevcut medya: {editingSlide?.mediaFileName}</span>
                          </div>
                        )}
                      </div>
                      {(selectedMediaUrl || mediaFile) && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => {
                            setMediaFile(null)
                            setSelectedMediaUrl(null)
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                  <Label htmlFor="isActive">Aktif</Label>
                </div>

                <div className="flex flex-col gap-2 pt-4">
                  <Button 
                    type="button" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white" 
                    disabled={submitting}
                    onClick={(e) => handleSubmit(e as any, true)}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        AI ile çeviriliyor ve kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Globe className="mr-2 h-4 w-4" />
                        {editingSlide ? 'AI ile Çevir ve Güncelle' : 'AI ile Çevir ve Ekle'}
                      </>
                    )}
                  </Button>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1" variant="secondary" disabled={submitting}>
                      {editingSlide ? 'Sadece Güncelle' : 'Sadece Ekle'}
                    </Button>
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                      İptal
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : slides.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            Henüz slider eklenmemiş.
          </Card>
        ) : (
          <div className="space-y-4">
            {slides.map((slide, index) => (
              <Card key={slide._id} className="p-4 flex gap-4 items-center flex-col sm:flex-row">
                <div className="h-32 w-48 bg-muted rounded overflow-hidden flex-shrink-0 relative">
                  {slide.mediaType === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-black/10">
                      <span className="text-xs font-medium">Video</span>
                    </div>
                  ) : (
                    <img
                      src={getMediaUrl(slide.mediaUrl)}
                      alt={typeof slide.title === 'string' ? slide.title : (slide.title?.tr || 'Slide')}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Badge variant={slide.isActive ? 'default' : 'secondary'}>
                      {slide.isActive ? 'Aktif' : 'Pasif'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">Sıra: {slide.order}</span>
                  </div>
                  <h3 className="font-medium truncate">
                    {typeof slide.title === 'string' ? slide.title : (slide.title?.tr || 'İsimsiz')}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {typeof slide.description === 'string' ? slide.description : (slide.description?.tr || '-')}
                  </p>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSlide(slide._id, 'up')}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSlide(slide._id, 'down')}
                    disabled={index === slides.length - 1}
                  >
                    <MoveDown className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center justify-center px-2 py-1">
                    <Switch
                      checked={slide.isActive}
                      onCheckedChange={() => toggleStatus(slide)}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(slide)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(slide._id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <MediaPicker
          open={isMediaPickerOpen}
          onOpenChange={setIsMediaPickerOpen}
          onSelect={(url) => {
            setSelectedMediaUrl(url)
            setMediaFile(null)
            setIsMediaPickerOpen(false)
          }}
          title="Slider Medyası Seç"
        />
      </Main>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/hero-slider/')({
  component: HeroSliderPage,
})
